"use client";

import React, { useState, useEffect, useMemo } from "react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/utils/format-price";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { useGeneraliRates } from "@/hooks/useGeneraliRates";
// @ts-ignore
import { COMUNI } from "codice-fiscale-js/src/lista-comuni.js";

export interface AssicurazioneGeneraliProps {
    codiceArticolo: string;
    prezzoAuto: number;
    immatricolazione: string;
    activeDispositivo: {
        nome: string;
        codice: string;
        prezzo: number;
        tipo: "satellitare" | "meccanico";
        durataMax: number;
    };
    sicurezzaDevices: any[];
    dispositiviSelezionati: any[];
    onGoBack: () => void;
    onDispositiviChange?: (dispositivi: any[]) => void;
    onChange?: (data: {
        selected: boolean;
        provincia: string;
        comune: string;
        durata: number;
        prezzo: number;
    }) => void;
    initialData?: {
        selected?: boolean;
        provincia?: string;
        comune?: string;
        durata?: number;
    };
}

export const AssicurazioneGenerali = ({
    codiceArticolo,
    prezzoAuto,
    immatricolazione,
    activeDispositivo,
    sicurezzaDevices,
    dispositiviSelezionati,
    onGoBack,
    onDispositiviChange,
    onChange,
    initialData,
}: AssicurazioneGeneraliProps) => {
    const calculateInsuranceNetAmount = (basePrice: number, rate: number) =>
        Number(((basePrice / 1000) * rate).toFixed(2));
    const applyInsuranceBracketAdjustment = (basePrice: number, netAmount: number) => {
        if (basePrice >= 1000 && basePrice <= 25000) {
            return Number(netAmount.toFixed(2));
        }

        if (basePrice > 25000 && basePrice <= 50000) {
            return Number((netAmount * 1.0165).toFixed(2));
        }

        if (basePrice >= 50001 && basePrice <= 100001) {
            return Number((netAmount * 1.0335).toFixed(2));
        }

        return Number(netAmount.toFixed(2));
    };

    // Insurance eligibility (max 6 years old)
    const currentYear = new Date().getFullYear();
    let carYear = currentYear;
    if (immatricolazione) {
        if (immatricolazione.includes("/")) {
            carYear = parseInt(immatricolazione.split("/")[2]);
        } else if (immatricolazione.includes("-")) {
            carYear = parseInt(immatricolazione.split("-")[0]);
        } else {
            carYear = parseInt(immatricolazione);
        }
    }
    const carAge = currentYear - carYear;
    const isInsuranceEligible = carAge <= 6;

    const maxDurata = activeDispositivo.durataMax || 60;

    // State
    const [wantsInsurance, setWantsInsurance] = useState(initialData?.selected ?? false);
    const [comune, setComune] = useState<string>(initialData?.comune ?? "");
    const [provincia, setProvincia] = useState<string>(initialData?.provincia ?? "CE");
    const [durataAssicurazione, setDurataAssicurazione] = useState<number>(initialData?.durata ?? 12);
    const [costoAssicurazione, setCostoAssicurazione] = useState<number>(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [openCombobox, setOpenCombobox] = useState(false);
    const [potentialSavings, setPotentialSavings] = useState<number | null>(null);
    const [noDeviceDialogOpen, setNoDeviceDialogOpen] = useState(false);
    const [selectedRate, setSelectedRate] = useState<number | null>(null);

    // Hook for Generali rates
    const { options: insuranceOptions, isLoading: isInsuranceLoading } = useGeneraliRates(
        codiceArticolo,
        provincia,
        wantsInsurance
    );

    // Default comune
    useEffect(() => {
        if (!comune) {
            const defaultCity = COMUNI.find((c: any) => c[2] === "CASERTA");
            if (defaultCity) {
                setComune(defaultCity[2]);
                setProvincia(defaultCity[1]);
            }
        }
    }, []);

    // Filter comuni
    const filteredComuni = useMemo(() => {
        if (!searchQuery || searchQuery.length < 2) return [];
        const normalizedQuery = searchQuery.toLowerCase();
        return COMUNI.filter((c: any) => c[2].toLowerCase().includes(normalizedQuery)).slice(0, 50);
    }, [searchQuery]);

    // Calculate insurance cost
    useEffect(() => {
        if (!wantsInsurance || insuranceOptions.length === 0 || !activeDispositivo.codice) {
            setCostoAssicurazione(0);
            setSelectedRate(null);
            return;
        }
        const tipoStr = activeDispositivo.tipo === "satellitare" ? "satellitare" : "meccanico";
        const option = insuranceOptions.find(
            (opt: any) => opt.tipo === tipoStr && opt.durata === durataAssicurazione
        );
        if (option) {
            const rate = parseFloat(option.importo);
            const netAmount = calculateInsuranceNetAmount(prezzoAuto, rate);
            const calculated = applyInsuranceBracketAdjustment(prezzoAuto, netAmount);
            setSelectedRate(rate);
            setCostoAssicurazione(calculated);
        } else {
            setSelectedRate(null);
            setCostoAssicurazione(0);
        }
    }, [prezzoAuto, insuranceOptions, activeDispositivo, durataAssicurazione, wantsInsurance]);

    // Se il costo totale è inferiore a 250€ la tariffa non è valida
    const isTariffaMinima = costoAssicurazione > 0 && costoAssicurazione < 250;
    // Costo effettivo applicato: minimo 250€ se sotto soglia
    const costoEffettivo = isTariffaMinima ? 250 : costoAssicurazione;

    // Calculate potential savings
    useEffect(() => {
        if (!wantsInsurance || insuranceOptions.length === 0 || activeDispositivo.tipo === "satellitare") {
            setPotentialSavings(null);
            return;
        }
        const satOption = insuranceOptions.find(
            (opt: any) => opt.tipo === "satellitare" && opt.durata === durataAssicurazione
        );
        if (satOption) {
            const satRate = parseFloat(satOption.importo);
            const satNetAmount = calculateInsuranceNetAmount(prezzoAuto, satRate);
            const satCost = applyInsuranceBracketAdjustment(prezzoAuto, satNetAmount);
            const savings = costoAssicurazione - satCost;
            setPotentialSavings(savings > 0 ? savings : null);
        } else {
            setPotentialSavings(null);
        }
    }, [costoAssicurazione, insuranceOptions, durataAssicurazione, activeDispositivo.tipo, prezzoAuto, wantsInsurance]);

    // Notify parent of changes
    // Se la tariffa è sotto soglia minima (250€) trattiamo come "non selezionata" nello store
    useEffect(() => {
        if (onChange) {
            onChange({
                selected: wantsInsurance,
                provincia,
                comune,
                durata: durataAssicurazione,
                prezzo: costoEffettivo,
            });
        }
    }, [wantsInsurance, provincia, comune, durataAssicurazione, costoEffettivo]);

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-6">
            {/* Header + Toggle */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                    <h3 className="text-xl font-bold text-gray-900">Assicurazione Furto e Incendio</h3>
                    <p className="text-sm text-gray-500">Proteggi la tua auto con le nostre tariffe convenzionate.</p>
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-3 sm:gap-4">
                    <div className="relative hidden h-8 w-28 sm:block sm:h-10 sm:w-36">
                        <Image
                            src="/generali-logo.png"
                            alt="Logo Generali"
                            fill
                            sizes="144px"
                            className="object-contain object-right"
                        />
                    </div>
                    <div className="flex shrink-0 items-center space-x-2 whitespace-nowrap">
                    {isInsuranceEligible ? (
                        <>
                            <Label htmlFor="insurance-mode" className="font-semibold">
                                {wantsInsurance ? "Si" : "No"}
                            </Label>
                            <Switch
                                id="insurance-mode"
                                checked={wantsInsurance}
                                onCheckedChange={(checked) => {
                                    if (checked && activeDispositivo.codice === "") {
                                        setNoDeviceDialogOpen(true);
                                        setWantsInsurance(false);
                                    } else {
                                        setWantsInsurance(checked);
                                    }
                                }}
                            />
                            <Dialog open={noDeviceDialogOpen} onOpenChange={setNoDeviceDialogOpen}>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle className="text-blue-600 text-xl font-bold">
                                            Dispositivo Antifurto Assente
                                        </DialogTitle>
                                        <DialogDescription className="pt-4 text-gray-700">
                                            Attenzione: i dispositivi antifurto sono obbligatori per attivare la polizza assicurativa Furto e Incendio. Non hai selezionato alcun dispositivo nello step precedente.
                                            <br /><br />
                                            Se desideri includere la polizza assicurativa, torna indietro e seleziona un dispositivo antifurto.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <DialogFooter className="sm:justify-center mt-4">
                                        <Button
                                            type="button"
                                            onClick={() => { setNoDeviceDialogOpen(false); onGoBack(); }}
                                            className="bg-blue-600 text-white hover:bg-blue-700 px-8"
                                        >
                                            Torna alla selezione
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={() => setNoDeviceDialogOpen(false)}
                                            variant="ghost"
                                            className="text-gray-500"
                                        >
                                            Chiudi
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </>
                    ) : (
                        <span className="max-w-full whitespace-normal break-words text-center text-sm font-semibold leading-tight text-red-500 bg-red-50 px-3 py-1 rounded-full sm:text-left">
                            Non attivabile (Veicolo &gt; 6 anni)
                        </span>
                    )}
                    </div>
                </div>
            </div>

            {/* Loading */}
            {isInsuranceLoading && (
                <div className="flex justify-center items-center py-4">
                    <span className="text-gray-500 animate-pulse">Caricamento tariffe in corso...</span>
                </div>
            )}

            {/* Insurance details */}
            {wantsInsurance && !isInsuranceLoading && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                    <div className="grid gap-3 md:grid-cols-[2fr_1fr]">
                        <div className="space-y-3">
                            {/* Comune */}
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-sm font-medium text-gray-700">Comune di residenza</p>
                                    {comune && (
                                        <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                                            {provincia}
                                        </span>
                                    )}
                                </div>
                                <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={openCombobox}
                                            className="w-full justify-between h-9 text-sm"
                                        >
                                            {comune ? comune : "Cerca comune..."}
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[300px] p-0">
                                        <Command shouldFilter={false}>
                                            <CommandInput
                                                placeholder="Cerca comune..."
                                                onValueChange={setSearchQuery}
                                            />
                                            <CommandList>
                                                {searchQuery.length < 2 && (
                                                    <div className="py-6 text-center text-sm">Digita per cercare...</div>
                                                )}
                                                {filteredComuni.map((city: any) => (
                                                    <CommandItem
                                                        key={city[0]}
                                                        value={city[2]}
                                                        onSelect={() => {
                                                            setComune(city[2]);
                                                            setProvincia(city[1]);
                                                            setOpenCombobox(false);
                                                        }}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                comune === city[2] ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        {city[2]} ({city[1]})
                                                    </CommandItem>
                                                ))}
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </div>

                            {/* Durata */}
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-sm font-medium text-gray-700">Durata</p>
                                    <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                                        {durataAssicurazione} mesi
                                    </span>
                                </div>
                                <Slider
                                    defaultValue={[durataAssicurazione]}
                                    max={maxDurata}
                                    min={12}
                                    step={12}
                                    onValueChange={(val) => setDurataAssicurazione(val[0])}
                                />
                                <div className="mt-2 flex justify-between text-xs text-gray-400">
                                    <span>12</span>
                                    <span>24</span>
                                    <span>36</span>
                                    {maxDurata > 36 && (
                                        <>
                                            <span>48</span>
                                            <span>60</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Cost display */}
                        {costoEffettivo > 0 ? (
                            <div className="flex h-full flex-col justify-center rounded-2xl border border-primary/30 bg-primary/5 p-5">
                                <p className="text-sm font-medium text-primary">Costo annuo stimato</p>
                                <div className="mt-3 flex items-end gap-2">
                                    <p className="text-4xl font-bold text-gray-900">
                                        {formatPrice(costoEffettivo / (durataAssicurazione / 12))}
                                    </p>
                                    <span className="text-base text-gray-500 mb-1">/ anno</span>
                                </div>
                                <p className="mt-3 text-xs text-gray-500">
                                    Totale per {durataAssicurazione} mesi:{" "}
                                    <strong>{formatPrice(costoEffettivo)}</strong>
                                </p>
                                <p className="mt-2 text-[11px] leading-relaxed text-gray-400">
                                    Tariffa convenzionata Generali. Salvo verifica dei dati definitivi.
                                </p>
                            </div>
                        ) : (
                            <div className="flex h-full flex-col justify-center rounded-2xl border border-gray-200 bg-gray-50 p-4 gap-2">
                                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M12 16v-4" />
                                        <path d="M12 8h.01" />
                                    </svg>
                                </div>
                                <p className="text-sm font-semibold text-gray-900">Tariffa non disponibile</p>
                                <p className="text-xs text-gray-500">
                                    Nessuna tariffa per la provincia di <strong>{provincia}</strong>.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Savings suggestion */}
                    {potentialSavings && potentialSavings > 0 && (
                        <div className="rounded-2xl border-2 border-blue-200 bg-blue-50/60 p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-100">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 16v-4" />
                                    <path d="M12 8h.01" />
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <h4 className="font-bold text-blue-900 text-sm">Risparmio disponibile</h4>
                                    <span className="text-sm font-bold text-blue-700 bg-blue-100 px-2.5 py-0.5 rounded-full">
                                        -{formatPrice(potentialSavings)}
                                    </span>
                                </div>
                                <p className="text-xs text-blue-700 mt-1">
                                    Con il <strong>Targa Telematics Ghost</strong> (satellitare) pagheresti meno sulla polizza.
                                </p>
                                <Button
                                    variant="link"
                                    className="text-blue-700 font-semibold p-0 h-auto mt-2 text-xs underline"
                                    onClick={() => {
                                        const ghostDevice = sicurezzaDevices.find(
                                            (d: any) => d.title.toLowerCase().includes('targa')
                                        );
                                        if (ghostDevice && onDispositiviChange) {
                                            const withoutSatellite = dispositiviSelezionati.filter(
                                                (d: any) => !d.title.toLowerCase().includes('targa')
                                            );
                                            onDispositiviChange([...withoutSatellite, ghostDevice]);
                                        }
                                    }}
                                >
                                    Aggiungi Targa Telematics Ghost &rarr;
                                </Button>
                            </div>
                        </div>
                    )}

                </div>
            )}
        </div>
    );
};
